/**
 * React Query bindings for the workspace module (`services/workspace.service.ts`).
 * The hooks only move data and refresh the list; what a screen says about the
 * outcome — a toast, a line in a dialog — is the screen's.
 *
 * Every key starts `["workspaces", userId]`: a list cached for one user must
 * never answer for the next one to sign in on this browser. A write refreshes
 * the whole `["workspaces"]` prefix.
 */

import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { isNotFound } from "@/lib/api-client";
import {
  checkWorkspaceDelete,
  createWorkspace,
  deleteWorkspace,
  getWorkspaceAllowance,
  listWorkspaces,
  renameWorkspace,
  type Workspace,
} from "@/services/workspace.service";

export type { Workspace } from "@/services/workspace.service";

const refresh = (queryClient: QueryClient) => queryClient.invalidateQueries({ queryKey: ["workspaces"] });

/** Every workspace the user can open — the switcher and Settings list it. */
export const useWorkspaces = () => {
  const { userId } = useWorkspace();
  return useQuery<Workspace[]>({ queryKey: ["workspaces", userId], queryFn: listWorkspaces, enabled: !!userId });
};

/** How many workspaces the user owns against what their plan allows. */
export const useWorkspaceAllowance = () => {
  const { userId } = useWorkspace();
  return useQuery({ queryKey: ["workspaces", userId, "allowance"], queryFn: getWorkspaceAllowance, enabled: !!userId });
};

/** Why `id` cannot be deleted yet, read afresh each time the delete dialog opens on it. */
export const useDeleteCheck = (id: string | undefined) => {
  const { userId } = useWorkspace();
  return useQuery({
    queryKey: ["workspaces", userId, "delete-check", id],
    queryFn: () => checkWorkspaceDelete(id!),
    enabled: !!userId && !!id,
    staleTime: 0,
    gcTime: 0,
  });
};

export const useCreateWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: (name: string) => createWorkspace(name), onSuccess: () => refresh(queryClient) });
};

export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameWorkspace(id, name),
    onSuccess: () => refresh(queryClient),
  });
};

/** A workspace already gone (404) refreshes the list too, so its row goes. */
export const useDeleteWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteWorkspace(id),
    onSuccess: () => refresh(queryClient),
    onError: (error) => {
      if (isNotFound(error)) void refresh(queryClient);
    },
  });
};
